import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, Users, Shield, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_PERMISSIONS = [
  { key: "events", label: "Veranstaltungen" },
  { key: "newsletter", label: "Newsletter" },
  { key: "jobs", label: "Karriere" },
  { key: "charts", label: "Charts" },
  { key: "contact", label: "Kontakt" },
  { key: "media", label: "Medien" },
  { key: "texts", label: "Texte" },
  { key: "users", label: "Benutzer" },
  { key: "audit", label: "Audit-Log" },
];

const emptyForm = { email: "", password: "", name: "", permissions: [] };

export default function AdminUsersTab({ currentUser, onUserUpdated }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const canManageUsers = (currentUser?.permissions || []).includes("users");

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      console.error("Users fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, permissions: ["events"] });
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      permissions: u.permissions || [],
    });
    setError("");
    setDialogOpen(true);
  };

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          email: form.email,
          name: form.name,
          permissions: form.permissions,
        };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/users/${editing.id}`, payload, { withCredentials: true });
        if (editing.id === currentUser?.id && onUserUpdated) onUserUpdated();
      } else {
        if (!form.password) {
          setError("Passwort ist erforderlich");
          setSaving(false);
          return;
        }
        await axios.post(`${API}/users`, form, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser?.id) {
      alert("Sie können sich nicht selbst löschen.");
      return;
    }
    if (!window.confirm(`Benutzer "${u.email}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`, { withCredentials: true });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading text-xl text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#C5993A]" /> Benutzerverwaltung
          </h3>
          <p className="text-xs text-[#3A4F60] mt-1">Admin-Benutzer und ihre Tab-Berechtigungen verwalten</p>
        </div>
        {canManageUsers && (
          <Button
            data-testid="create-user-button"
            onClick={openCreate}
            className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Neuer Benutzer
          </Button>
        )}
      </div>

      <div className="space-y-3" data-testid="admin-users-list">
        {users.map((u) => {
          const isSelf = u.id === currentUser?.id;
          const canEdit = canManageUsers || isSelf;
          return (
            <div
              key={u.id}
              data-testid={`admin-user-${u.id}`}
              className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white font-medium truncate">{u.email}</p>
                  {isSelf && (
                    <span className="text-[10px] uppercase tracking-wider bg-[#C5993A]/20 text-[#C5993A] px-2 py-0.5 rounded">Sie</span>
                  )}
                </div>
                {u.name && <p className="text-[#8899AA] text-sm truncate">{u.name}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(u.permissions || []).length === 0 ? (
                    <span className="text-xs text-[#3A4F60] italic">Keine Tab-Berechtigungen</span>
                  ) : (
                    (u.permissions || []).map((p) => {
                      const def = AVAILABLE_PERMISSIONS.find((x) => x.key === p);
                      return (
                        <span
                          key={p}
                          className="text-[10px] bg-[#0C1620] border border-[#3A4F60]/40 text-[#8899AA] px-2 py-0.5 rounded flex items-center gap-1"
                        >
                          <Shield className="w-2.5 h-2.5 text-[#C5993A]" /> {def?.label || p}
                        </span>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {canEdit && (
                  <Button
                    data-testid={`edit-user-${u.id}`}
                    onClick={() => openEdit(u)}
                    variant="ghost"
                    size="sm"
                    className="text-[#8899AA] hover:text-[#C5993A]"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                {canManageUsers && !isSelf && (
                  <Button
                    data-testid={`delete-user-${u.id}`}
                    onClick={() => handleDelete(u)}
                    variant="ghost"
                    size="sm"
                    className="text-[#8899AA] hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-lg" aria-describedby="user-dialog-desc">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? "Benutzer bearbeiten" : "Neuen Benutzer anlegen"}
            </DialogTitle>
            <p id="user-dialog-desc" className="sr-only">Benutzerformular</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">E-Mail *</label>
              <Input
                data-testid="user-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">Name</label>
              <Input
                data-testid="user-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" /> Passwort {editing ? "(leer lassen = unverändert)" : "*"}
              </label>
              <Input
                data-testid="user-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Nur ausfüllen zum Ändern" : ""}
                className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]"
              />
            </div>
            {canManageUsers && (
              <div className="border-t border-[#3A4F60]/30 pt-4">
                <label className="block text-sm text-[#8899AA] mb-2 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Tab-Berechtigungen
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_PERMISSIONS.map((p) => {
                    const checked = form.permissions.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        data-testid={`perm-${p.key}`}
                        className={`flex items-center gap-2 p-2.5 rounded border cursor-pointer transition-colors ${
                          checked
                            ? "border-[#C5993A]/60 bg-[#C5993A]/10"
                            : "border-[#3A4F60]/40 bg-[#0C1620] hover:border-[#3A4F60]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePerm(p.key)}
                          className="accent-[#C5993A] w-4 h-4"
                        />
                        <span className="text-sm text-white">{p.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {error && <p data-testid="user-form-error" className="text-red-400 text-sm">{error}</p>}
          </div>
          <DialogFooter>
            <Button
              data-testid="cancel-user-button"
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-[#8899AA]"
            >
              Abbrechen
            </Button>
            <Button
              data-testid="save-user-button"
              onClick={handleSave}
              disabled={saving || !form.email}
              className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
            >
              {saving ? "Speichern..." : editing ? "Aktualisieren" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
